import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';

const EMPTY = {
  name: '',
  type: 'general',
  date: '',
  jurisdiction_id: '',
  status: 'scheduled',
  description: '',
  source_url: '',
};

const TYPES = [
  { value: 'primary', label: 'Primary' },
  { value: 'general', label: 'General' },
  { value: 'runoff', label: 'Runoff' },
  { value: 'special', label: 'Special' },
  { value: 'municipal', label: 'Municipal' },
];

const STATUSES = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'canceled', label: 'Canceled' },
];

export default function ElectionFormPage({ mode = 'create' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [stateJurisdictions, setStateJurisdictions] = useState([]);
  const [countyJurisdictions, setCountyJurisdictions] = useState([]);
  // UX shortcut: pick a state-jurisdiction first; if a county is chosen,
  // we swap jurisdiction_id to that county. Statewide elections keep state.
  const [pickerStateId, setPickerStateId] = useState('');
  const [pickerCountyId, setPickerCountyId] = useState('');

  // Load all state-level jurisdictions on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await adminApi.get('/admin/jurisdictions?type=state&page_size=200');
        setStateJurisdictions(res.data.items || []);
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  // Load counties when state picker changes
  useEffect(() => {
    if (!pickerStateId) { setCountyJurisdictions([]); return; }
    (async () => {
      try {
        const res = await adminApi.get(`/admin/jurisdictions?type=county&parent_jurisdiction_id=${pickerStateId}&page_size=500`);
        setCountyJurisdictions(res.data.items || []);
      } catch (_e) { setCountyJurisdictions([]); }
    })();
  }, [pickerStateId]);

  // Sync jurisdiction_id when picker changes
  useEffect(() => {
    const target = pickerCountyId || pickerStateId || '';
    if (target && target !== data.jurisdiction_id) {
      setData((d) => ({ ...d, jurisdiction_id: target }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerStateId, pickerCountyId]);

  useEffect(() => {
    if (mode !== 'edit') return;
    let active = true;
    (async () => {
      try {
        const res = await adminApi.get(`/admin/elections/${id}`);
        if (!active) return;
        const e = res.data;
        setData({
          name: e.name || '',
          type: e.type || 'general',
          date: e.date || '',
          jurisdiction_id: e.jurisdiction_id || '',
          status: e.status || 'scheduled',
          description: e.description || '',
          source_url: e.source_url || '',
        });
        // back-fill picker selections
        const j = e.jurisdiction;
        if (j) {
          if (j.type === 'county') {
            setPickerStateId(j.parent_jurisdiction_id || '');
            setPickerCountyId(e.jurisdiction_id);
          } else {
            setPickerStateId(e.jurisdiction_id);
          }
        }
      } catch (e) {
        if (active) setError(extractError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id, mode]);

  function update(field, value) {
    setData((d) => ({ ...d, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!data.name.trim()) { setError('Name is required.'); return; }
    if (!data.date) { setError('Date is required (YYYY-MM-DD).'); return; }
    if (!data.jurisdiction_id) { setError('Jurisdiction (state, or county) is required.'); return; }
    setSaving(true);
    try {
      const payload = {
        name: data.name.trim(),
        type: data.type,
        date: data.date,
        jurisdiction_id: data.jurisdiction_id,
        status: data.status,
        description: data.description?.trim() || '',
        source_url: data.source_url?.trim() || '',
      };
      if (mode === 'create') {
        const res = await adminApi.post('/admin/elections', payload);
        toast.success('Election day created');
        navigate(`/admin/elections/${res.data.id}`);
      } else {
        await adminApi.put(`/admin/elections/${id}`, payload);
        toast.success('Election day updated');
        navigate(`/admin/elections/${id}`);
      }
    } catch (err) {
      const msg = extractError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <><PageHeader title="Election" /><div className="text-center p-5"><Spinner animation="border" /></div></>;

  return (
    <>
      <PageHeader
        title={mode === 'create' ? 'New election day' : `Edit election day`}
        breadcrumb={<span><Link to="/admin/elections" className="text-decoration-none">Election Days</Link> / {mode === 'create' ? 'New' : 'Edit'}</span>}
        actions={
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={() => navigate('/admin/elections')} data-testid="election-form-cancel-button">Cancel</Button>
            <Button variant="primary" onClick={handleSubmit} disabled={saving} data-testid="election-form-save-button">
              {saving ? <Spinner size="sm" animation="border" /> : 'Save'}
            </Button>
          </div>
        }
      />
      <div className="page-content">
        {error && <Alert variant="danger" data-testid="election-form-error">{error}</Alert>}
        <Form onSubmit={handleSubmit} noValidate>
          <Card>
            <Card.Body>
              <Row className="g-3">
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Election day name <span className="text-danger">*</span></Form.Label>
                    <Form.Control value={data.name} onChange={(e) => update('name', e.target.value)} placeholder="e.g. 2026 Florida Primary" data-testid="election-name-input" />
                    <Form.Text>One row per voting day. Add specific contests as Races on the next screen.</Form.Text>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>State (jurisdiction) <span className="text-danger">*</span></Form.Label>
                    <Form.Select value={pickerStateId} onChange={(e) => { setPickerStateId(e.target.value); setPickerCountyId(''); }} data-testid="election-state-select">
                      <option value="">— Select state —</option>
                      {stateJurisdictions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>County <span className="text-muted">(optional — leave blank for statewide)</span></Form.Label>
                    <Form.Select value={pickerCountyId} onChange={(e) => setPickerCountyId(e.target.value)} disabled={!pickerStateId} data-testid="election-county-select">
                      <option value="">— Statewide —</option>
                      {countyJurisdictions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label>Election date <span className="text-danger">*</span></Form.Label>
                    <Form.Control type="date" value={data.date || ''} onChange={(e) => update('date', e.target.value)} data-testid="election-date-input" />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label>Type <span className="text-danger">*</span></Form.Label>
                    <Form.Select value={data.type} onChange={(e) => update('type', e.target.value)} data-testid="election-type-select">
                      {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Select value={data.status} onChange={(e) => update('status', e.target.value)} data-testid="election-status-select">
                      {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Source URL</Form.Label>
                    <Form.Control value={data.source_url || ''} onChange={(e) => update('source_url', e.target.value)} placeholder="https://..." data-testid="election-source-url-input" />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control as="textarea" rows={2} value={data.description || ''} onChange={(e) => update('description', e.target.value)} data-testid="election-description-input" />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Form>
      </div>
    </>
  );
}
